import React, { useMemo, useState, ComponentType } from 'react'
import { OptionProps, Options, GroupBase, PlaceholderProps } from 'react-select'
import { Checkbox } from '../../controls/Checkbox/Checkbox'
import { SimpleSelect, SimpleSelectProps, SimpleSelectOption } from '../SimpleSelect/SimpleSelect'
import styled, { StyledProps } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ClearIndicatorStyled } from '../BasicSelect/styles'

// eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
const noop = () => {}

const OptionStyled = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
  margin: 0;
  cursor: pointer;
  color: ${props => props.theme.color.onPrimaryDark};
  transition: 0.3s background-color;
  font-weight: 500;
  line-height: 20px;
  min-height: 40px;
  font-size: 14px;
  border-radius: 4px;
  padding: 10px 8px;
  box-sizing: border-box;
  align-items: start;

  &[data-disabled='true'] {
    pointer-events: none;

    span {
      opacity: 0.7;
    }

    &:hover {
      background-color: transparent;
    }
  }

  &:hover {
    background-color: ${props => props.theme.color.primary50};

    button[role='checkbox'] {
      border-color: ${props => props.theme.color.primary500};
    }
  }

  &[data-nested-level='0'] {
    font-weight: 700;
  }

  &[data-nested-level='1'] {
    font-weight: 600;
    margin-left: 12px;
  }

  &[data-nested-level='2'] {
    margin-left: 24px;
  }
`

const PlaceholderStyled = styled.div`
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif;
  color: ${(props: StyledProps<{ all: boolean }>) => (props.all ? '#808080' : props.theme.color.onPrimaryDark)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 40px);
  position: absolute;
  left: 10px;
`

const ClearButtonStyled = styled(ClearIndicatorStyled)`
  position: absolute;
  right: 30px;
  bottom: 11px;
`

const Wrapper = styled.div`
  position: relative;
`

export type CheckboxSelectOption = SimpleSelectOption & {
  isParentSelected?: boolean
  nestedLevel?: '0' | '1' | '2'
  labelToRender?: React.ReactNode
}

export type CheckboxSelectProps = SimpleSelectProps & {
  options: Options<CheckboxSelectOption>
  selected: string[]
  onChange: (id: string) => void
  onClear?: () => void
  noneSelected?: string
  isSearchable?: boolean
  singleSelected?: string
  onSearchChange?: (value: string) => void
  onMenuClose?: () => void
}

export const CheckboxSelect: React.FC<CheckboxSelectProps> = ({
  selected,
  onChange,
  options,
  isSearchable,
  onClear,
  singleSelected,
  noneSelected,
  onSearchChange,
  onMenuClose,
  ...props
}) => {
  // custom search since react-select has problems with it https://github.com/JedWatson/react-select/issues/4133
  const [search, setSearch] = useState('')
  const { t } = useTranslation('ui')
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    e.bubbles = false
    onClear && onClear()
  }

  const Option: ComponentType<OptionProps<CheckboxSelectOption, boolean, GroupBase<CheckboxSelectOption>> & {
    value: string
  }> = ({ value, data, label }) => {
    return (
      <OptionStyled
        onClick={() => onChange(value)}
        id={value}
        data-nested-level={data.nestedLevel || ''}
        data-disabled={data.isParentSelected}
      >
        <Checkbox
          onChange={noop}
          isChecked={selected.includes(value) || data.isParentSelected}
          disabled={data.isParentSelected}
        />
        <span>{data.labelToRender || label}</span>
      </OptionStyled>
    )
  }
  const Placeholder: ComponentType<PlaceholderProps<
    CheckboxSelectOption,
    boolean,
    GroupBase<CheckboxSelectOption>
  >> = ({ selectProps }) => {
    return (
      <PlaceholderStyled all={selected.length === 0}>
        {selected.length > 0
          ? singleSelected || t('CheckboxSelect.multipleSelected', { items: selectProps.placeholder })
          : noneSelected
          ? noneSelected
          : t('CheckboxSelect.allSelected', { items: selectProps.placeholder })}
      </PlaceholderStyled>
    )
  }

  const filteredOptions = useMemo(() => {
    if (!search) {
      return options
    }
    return options.filter(option =>
      search && typeof option.title === 'string' ? option.title.toLowerCase().includes(search.toLowerCase()) : true,
    )
  }, [options, search])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    onSearchChange && onSearchChange(value)
  }

  const handleMenuClose = () => {
    setSearch('')
    onMenuClose && onMenuClose()
  }

  return (
    <Wrapper>
      <SimpleSelect
        {...props}
        onChange={noop}
        options={filteredOptions}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        components={{ Option, Placeholder }}
        onSearchChange={handleSearchChange}
        onMenuClose={handleMenuClose}
        filterOption={() => true}
        isSearchable={isSearchable}
        noneSelected={noneSelected}
      />
      {!!selected.length && !!onClear && <ClearButtonStyled onClick={handleClear} />}
    </Wrapper>
  )
}
